/**
 * Application shell: a side rail, a page header, and the main region.
 *
 * Shared by both applications so the chrome, the skip link and the landmark structure are identical —
 * an employee moving between the assistant and the back office should not have to relearn the page,
 * and an accessibility fix should only need making once.
 *
 * ## The rail replaced a top navigation bar
 *
 * Both mockups (`design/bank_ai_assistant_chat`, `design/admin_conversation_supervision`) put
 * navigation in a persistent rail. A top bar was adequate for three fixed destinations and wrong for
 * what the assistant actually needs on the left: a *conversation history*, grouped by recency, of
 * unpredictable length. A list does not fit in a horizontal bar.
 *
 * `sidebar` is optional, and its absence is not a fallback for a screen that forgot it — the
 * sign-in and forced-password-change screens render outside this shell entirely, because a rail full
 * of destinations an anonymous visitor cannot reach is worse than no rail.
 *
 * ## Direction
 *
 * The rail is a flex sibling of the main region rather than a positioned element, so the browser
 * mirrors the order for RTL for free. Every alternative needed a logical property and a second look.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

export interface AppShellProps {
  /** Application name, already translated by the caller from its `app` namespace. */
  title: string;
  /**
   * The side rail. Usually a `SideNav`.
   *
   * Renders its own `nav` landmark, which is why this slot does not wrap it in one — two nested
   * navigation landmarks make a screen reader announce the same region twice.
   */
  sidebar?: ReactNode;
  /**
   * Header actions, inline-end. The language toggle and the session menu.
   *
   * The header is thin on purpose: the mockups put the current page's title on the left and controls
   * on the right, with navigation in the rail rather than here.
   */
  actions?: ReactNode;
  /**
   * What the header says, when the page is more specific than the application.
   *
   * The mockups show the *conversation* title in the assistant and the *screen* name in the back
   * office, not the product name — an employee already knows which application they opened.
   */
  heading?: ReactNode;
  children: ReactNode;
  /**
   * Lets the main region own its own scrolling and padding.
   *
   * The chat surface needs this: its composer is pinned to the bottom and its transcript scrolls
   * independently, which the shell's own padded, page-scrolling main region cannot express.
   */
  bare?: boolean;
}

/**
 * Where `ShellPanel` puts its children: a full-height column at the inline end, beside the
 * header-and-main column rather than inside it.
 *
 * It has to be outside `main` to be full height. Rendered inside, a panel starts *below* the page
 * header, so its own title sits 72px lower than the page title and the two rules across the top of
 * the window never meet — which the design has level.
 *
 * It is reached by portal rather than by a prop because the state belongs to the page. The chat
 * page decides which citation is open; the knowledge base decides whether the preview is showing.
 * Passing those up to the shell would mean lifting a page's state into the application shell so the
 * shell could hand it back, and every page would pay for a slot two of them use.
 */
const PANEL_SLOT_ID = "nab-shell-panel";

/**
 * Move focus to the page heading whenever the route changes.
 *
 * The part of single-page navigation that everyone skips. Clicking a nav item replaces the whole
 * main region while focus stays on the link that was clicked: a screen-reader user hears nothing
 * and has no idea the page changed, and a keyboard user's next Tab resumes in the rail rather than
 * in the content they just asked for.
 *
 * The heading is the right target because it is also the announcement — moving focus to an `h1`
 * makes a screen reader read the name of the screen, which is exactly what a sighted user gets from
 * watching the page change.
 *
 * Skipped on first render. On a fresh page load the browser has already put focus where it belongs
 * and stealing it would scroll past the skip link before anybody could use it.
 */
function useHeadingFocus(key: unknown) {
  const heading = useRef<HTMLHeadingElement>(null);
  const mounted = useRef(false);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    heading.current?.focus();
  }, [key]);

  return heading;
}

const MAIN_CONTENT_ID = "nab-main-content";

export function AppShell({
  title,
  sidebar,
  actions,
  heading,
  children,
  bare = false,
}: AppShellProps): JSX.Element {
  const { t } = useTranslation();
  // Keyed on the heading itself: it is what changes when the route does, and it also changes when a
  // conversation is renamed — which is a page change as far as a reader is concerned.
  const headingRef = useHeadingFocus(heading);

  return (
    // `h-screen` with `overflow-hidden`, not `min-h-screen`: the rail and the main region scroll
    // independently, and a page that also scrolls gives you two scrollbars and a header that leaves.
    <div className="flex h-screen overflow-hidden bg-surface">
      {/* Keyboard users get past the rail without tabbing through every conversation in it. */}
      <a
        href={`#${MAIN_CONTENT_ID}`}
        className="absolute start-4 top-[-100%] z-toast rounded-md bg-primary px-4 py-2 text-onPrimary focus:top-2"
      >
        {t("a11y.skipToContent")}
      </a>

      {sidebar}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-header shrink-0 items-center gap-4 border-b border-borderSubtle bg-surfaceRaised px-6">
          <div className="flex min-w-0 flex-col">
            {heading ? (
              // The screen's `h1`, not a styled span. The mockups put the page name here and not in
              // the body, so pages do not repeat it — and a screen whose only visible title was a
              // `span` would leave a screen-reader user with no top-level heading to navigate to.
              //
              // Blue and bold, per the charte: this is the one piece of brand colour on an otherwise
              // neutral header, and it is what tells an employee which screen they are on.
              // `tabIndex={-1}` makes it focusable programmatically without putting it in the tab
              // order, so it can be the target of a route change and is otherwise invisible to a
              // keyboard user moving through the page.
              <h1
                ref={headingRef}
                tabIndex={-1}
                className="truncate text-heading-sm font-bold tracking-tight text-primary outline-none"
              >
                {heading}
              </h1>
            ) : (
              <>
                <span className="text-label-sm uppercase tracking-wide text-textMuted">
                  {t("brand.bank")}
                </span>
                <span className="truncate text-heading-sm font-bold text-primary">{title}</span>
              </>
            )}
          </div>

          {/* Pushes the actions to the far edge — inline-end, so it mirrors. */}
          <div className="flex-1" />

          <div className="flex shrink-0 items-center gap-3">{actions}</div>
        </header>

        <main
          id={MAIN_CONTENT_ID}
          className={
            bare
              ? "flex min-h-0 flex-1 flex-col overflow-hidden bg-surfaceRaised"
              : "min-h-0 flex-1 overflow-y-auto bg-surfaceRaised p-6"
          }
        >
          {children}
        </main>
      </div>

      {/* `flex` and nothing else: with no panel portalled in it is a zero-width flex item that
          paints nothing. Every border and width belongs to the panel, not to the slot. */}
      <div id={PANEL_SLOT_ID} className="flex" />
    </div>
  );
}

/**
 * Render a full-height panel at the inline end of the shell.
 *
 * Use it anywhere inside an `AppShell`; the children appear beside the main region rather than
 * inside it. Renders nothing until the slot exists, which is the first paint — hence the state
 * below rather than a straight `document.getElementById`.
 */
export function ShellPanel({ children }: { children: ReactNode }): JSX.Element | null {
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  // On mount, not during render: the slot is a sibling rendered by `AppShell`, so on the very first
  // pass it does not exist yet. An effect runs after that pass, when it does.
  useEffect(() => {
    setSlot(document.getElementById(PANEL_SLOT_ID));
  }, []);

  return slot ? createPortal(children, slot) : null;
}
